import {startNodeBlueprint} from "@/pages/flow/NodeTypes/Blueprints";
import {createNode} from "@/pages/flow/NodeTypes/FlowNode";
import {FlowNodeTypes} from "@/types/node";
import {getNodesByType, syncNodeActions} from "@/utils/index";
import {Edge, Node} from "@xyflow/react";

// Helper functions
const deepClone = (node: Node) => {
  return JSON.parse(JSON.stringify(node));
};

const cleanDuplicateIds = (set: any) => {
  return set.filter(
    (item, index, self) => index === self.findLastIndex(t => t.id === item.id),
  );
};

// Migrations
const migration_1 = (nodes: Node[], edges: Edge[]) => {
  // Set node position to 0,0
  nodes.forEach(node => {
    node.position = {x: 0, y: 0};
  });

  const startNode = getNodesByType(nodes, FlowNodeTypes.START)[0];
  const globalNode = getNodesByType(nodes, FlowNodeTypes.GLOBAL);
  if (globalNode.length > 0) {
    return {nodes, edges};
  }

  if (startNode) {
    const globalNode = deepClone(startNode);
    const scriptedGreeting = startNode.data.settings["scripted_greeting"];

    globalNode.type = FlowNodeTypes.GLOBAL;
    globalNode.data.type = FlowNodeTypes.GLOBAL;
    globalNode.id = startNode.id;
    globalNode.selected = false;

    delete startNode.data.settings["global_instructions"];

    nodes = nodes.filter(node => node.type !== FlowNodeTypes.START);
    nodes.push(globalNode);

    const greetNode = createNode(startNodeBlueprint);
    greetNode.data.settings["scripted_greeting"] = scriptedGreeting;

    nodes.push(greetNode);

    const greetEdge = {
      id: `${globalNode.id}->${greetNode.id}`,
      source: globalNode.id,
      target: greetNode.id,
      type: "workflow",
    };

    const startEdges = edges.filter(edge => edge.source === startNode.id);

    startEdges.forEach(edge => {
      edge.source = greetNode.id;
      edge.id = `${greetNode.id}->${edge.target}`;
    });

    edges.push(...startEdges);
    edges.push(greetEdge);
  }

  return {nodes, edges};
};

const migration_2 = (nodes: Node[], edges: Edge[]) => {
  const stepNodes = getNodesByType(nodes, FlowNodeTypes.STEP);

  if (stepNodes.length === 0) {
    return {nodes, edges};
  }

  if (stepNodes.length > 0) {
    stepNodes.forEach(stepNode => {
      const stepNodeClone = deepClone(stepNode);

      stepNodeClone.type = FlowNodeTypes.COLLECT_VARIABLES;
      stepNodeClone.data.type = FlowNodeTypes.COLLECT_VARIABLES;
      stepNodeClone.data.nodeTitle = "Instruction";
      stepNodeClone.data.settingsTitle = "Instruction";
      stepNodeClone.data.description = "This is the instruction node";

      nodes.push(stepNodeClone);
    });
  }

  return {
    nodes: nodes.filter(node => node.type !== FlowNodeTypes.STEP),
    edges,
  };
};

const migration_3 = (nodes: Node[], edges: Edge[]) => {
  const instructionNodes = getNodesByType(nodes, FlowNodeTypes.INSTRUCTION);
  if (instructionNodes.length === 0) {
    return {nodes, edges};
  }

  instructionNodes.forEach(instructionNode => {
    instructionNode.data.name = "Collect Variables";
    instructionNode.data.nodeTitle = "Collect Variables";
    instructionNode.data.settingsTitle = "Collect Variables";
    instructionNode.data.description = "This is the collect variables node";
    instructionNode.type = FlowNodeTypes.COLLECT_VARIABLES;
    instructionNode.data.type = FlowNodeTypes.COLLECT_VARIABLES;
  });
  return {nodes, edges};
};

// Versions
const versions = [
  {
    version: 1,
    apply: migration_1,
  },
  {
    version: 2,
    apply: migration_2,
  },
  {
    version: 3,
    apply: migration_3,
  },
];

// Apply migrations
export const applyFlowMigration = async (
  flowData: any,
  syncActions: boolean = false,
) => {
  const version = flowData?.version;
  if (!version && version !== 0) return flowData;

  const updatedFlowData = flowData || {};

  for (let i = version; i < versions.length; i++) {
    const nodes = updatedFlowData.nodes;
    const edges = updatedFlowData.edges;

    const {nodes: updatedNodes, edges: updatedEdges} = versions[i].apply(
      nodes,
      edges,
    );

    // Cleanup duplicate ids from nodes and edges
    updatedFlowData.nodes = cleanDuplicateIds(updatedNodes);
    updatedFlowData.edges = cleanDuplicateIds(updatedEdges);
    updatedFlowData.version = versions[i].version;
  }

  if (syncActions) {
    updatedFlowData.nodes = await syncNodeActions(updatedFlowData.nodes);
  }

  return updatedFlowData;
};

export const LATEST_FLOW_DATA_VERSION = versions[versions.length - 1].version;
